import {Component, computed, effect, inject, input, signal, untracked} from '@angular/core';
import {TranslocoPipe} from '@jsverse/transloco';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {Card} from 'primeng/card';
import {
  TenderPhaseType,
  TenderStatus,
  ProviderTenderControllerService,
  GetTenderDetailsRequestParams,
} from '@frontend/shared-api';
import {TabStatusEnum} from '../../../../../shared/tabs/tab-status.model';
import {TenderInvitationMailComponent} from '../../components/tender-invitation-mail/tender-invitation-mail.component';
import {TenderRfqGuidanceComponent} from '../tender-rfq-3-guidance/tender-rfq-guidance.component';
import {TenderRfqProposalParametersComponent} from '../tender-rfq-2-proposal-parameters/tender-rfq-proposal-parameters.component';
import {TenderDecisionComponent} from '../../components/tender-decision/tender-decision.component';
import {TenderRfqPriceListComponent} from '../tender-rfq-5-price-list/tender-rfq-price-list.component';
import {TenderRfqDocumentUploadComponent} from '../tender-rfq-6-documents/tender-rfq-document-upload.component';
import {rxResource} from '@angular/core/rxjs-interop';
import {LoadingComponent} from '../../../../../shared/components/loading/loading.component';
import {TenderRfqProposalSystemIntegrationComponent} from '../tender-rfq-25-system-integration/tender-rfq-proposal-system-integration.component';
import {Button} from 'primeng/button';
import {SubsectionTitleComponent} from '../../../../../shared/components/header/subsection-title/subsection-title.component';
import {TenderStatusUtils} from '../../../../../phase/tender-status.util';
import {WizardPhaseHeaderComponent} from '../../components/wizard-phase-header/wizard-phase-header.component';
import {TAB_INDICES} from './rfq-tender-wizard.types';
import {createTabManagementSignals} from '../../../../../shared/tabs/tab-utils';
import {TabStatusDirective} from '../../../../../shared/tabs/tab-status.directive';
import {TabsUnsavedRevealDirective} from '../../../../../shared/tabs/tabs-unsaved-reveal.directive';

@Component({
  selector: 'app-rfq-tender-wizard',
  imports: [
    TranslocoPipe,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    Card,
    TabPanel,
    TenderInvitationMailComponent,
    TenderRfqGuidanceComponent,
    TenderRfqProposalParametersComponent,
    TenderDecisionComponent,
    TenderRfqPriceListComponent,
    TenderRfqDocumentUploadComponent,
    LoadingComponent,
    TenderRfqProposalSystemIntegrationComponent,
    Button,
    SubsectionTitleComponent,
    WizardPhaseHeaderComponent,
    TabStatusDirective,
    TabsUnsavedRevealDirective,
  ],
  templateUrl: './rfq-tender-wizard.component.html',
})
export class RfqTenderWizardComponent {
  protected readonly TAB_INDICES = TAB_INDICES;
  protected readonly TenderPhaseType = TenderPhaseType;

  private readonly providerTenderService = inject(ProviderTenderControllerService);

  protected readonly tabManagement = createTabManagementSignals();
  protected readonly mailPreviewTabStatus = signal(TabStatusEnum.COMPLETED);
  protected readonly proposalParametersTabStatus = signal(TabStatusEnum.COMPLETED);
  protected readonly guidanceTabStatus = signal(TabStatusEnum.COMPLETED);
  protected readonly systemIntegrations = signal(TabStatusEnum.COMPLETED);
  protected readonly confirmationTabStatus = signal(TabStatusEnum.NOT_STARTED);
  protected readonly priceListTabStatus = signal(TabStatusEnum.NOT_STARTED);
  protected readonly documentTabStatus = signal(TabStatusEnum.NOT_STARTED);

  readonly tenderId = input.required<number>();
  readonly openDecision = input(false);

  protected readonly tenderDetailsResource = rxResource({
    params: (): GetTenderDetailsRequestParams => ({
      tenderId: this.tenderId(),
      tenderPhase: TenderPhaseType.RFQ,
    }),
    stream: ({params: request}) => this.providerTenderService.getTenderDetails(request),
  });

  protected tenderDetails = computed(() => this.tenderDetailsResource.value());

  protected readonly canEditDecision = computed(() =>
    TenderStatusUtils.canEditDecision(this.tenderDetails()?.status),
  );

  protected readonly projectAccepted = computed<boolean>(() => {
    return this.tenderDetails()?.status === TenderStatus.ACCEPTED;
  });

  constructor() {
    effect(() => {
      if (this.projectAccepted()) {
        this.confirmationTabStatus.set(TabStatusEnum.COMPLETED);
      }
    });
    effect(() => {
      if (this.openDecision() && this.canEditDecision()) {
        untracked(() => this.onTabChange(TAB_INDICES.CONFIRMATION));
      }
    });
  }

  get activeTabIndex() {
    return this.tabManagement.activeTabIndex.asReadonly();
  }

  onTabChange(tabIndex: string | number): void {
    this.tabManagement.onTabChange(tabIndex);
  }

  onTenderAccepted(tabIndex: number): void {
    this.tenderDetailsResource.reload();
    this.onTabChange(tabIndex);
  }

  onFinishPriceList(number: number) {
    this.priceListTabStatus.set(TabStatusEnum.COMPLETED);
    this.onTabChange(number);
  }
}
